type DataRow = {
  // Arbitrary keys allowed, but we assume at least:
  timestamp: string // ISO date
  value: number
  // Common keys (optional):
  metric_id?: string
  data_id?: string | undefined
  chain_id?: string | undefined
  product_id?: string | undefined
  market_sector?: string | undefined
  // ...and any others
  [k: string]: string | number | undefined
}

type SortedEntry = {
  data_id?: string | undefined
  chain_id?: string | undefined
  product_id?: string | undefined
  market_sector?: string | undefined
}

export type Payload =
  | {
      data: DataRow[]
      sorted_data_ids: SortedEntry[]
    }
  | undefined

type AggregateOptions = {
  limit: number // N
  otherLabel?: string // default "other"
  // If true, aggregate "other" across all metric_id values together
  // (i.e., ignore metric_id when summing). Default false (preserve metric_id).
  aggregateAcrossMetrics?: boolean
}

/**
 * Pre-compute key mappings from sorted_data_ids to data row keys.
 * Returns a map: selector key -> array of possible data keys
 */
function precomputeKeyMappings(sorted: SortedEntry[]): Map<string, string[]> {
  const mappings = new Map<string, string[]>()
  for (const selector of sorted) {
    for (const k of Object.keys(selector)) {
      if (!mappings.has(k)) {
        // market_sector_id in sorted_data_ids maps to market_sector in data
        if (k === "market_sector_id") {
          mappings.set(k, ["market_sector", "market_sector_id"])
        } else {
          mappings.set(k, [k])
        }
      }
    }
  }
  return mappings
}

/**
 * Build optimized matcher for "top-N" entries.
 * Uses pre-computed key mappings and optimized matching logic.
 */
function buildTopMatcher(
  sorted: SortedEntry[],
  limit: number,
  keyMappings: Map<string, string[]>
): (row: DataRow) => boolean {
  const top = sorted.slice(0, Math.max(0, limit))
  if (top.length === 0) {
    return () => false
  }

  // Pre-compute selector constraints for faster matching
  const selectors = top.map((sel) => {
    const constraints: Array<{ keys: string[]; value: string }> = []
    for (const k in sel) {
      const keys = keyMappings.get(k) || [k]
      constraints.push({ keys, value: String(sel[k as keyof SortedEntry]) })
    }
    return constraints
  })

  return (row: DataRow) => {
    // Fast path: check each selector's constraints
    for (const constraints of selectors) {
      let matches = true
      for (const { keys, value } of constraints) {
        let found = false
        for (const key of keys) {
          if (key in row && String(row[key]) === value) {
            found = true
            break
          }
        }
        if (!found) {
          matches = false
          break
        }
      }
      if (matches) return true
    }
    return false
  }
}

/**
 * Collect all non-aggregate keys seen in data.
 * Optimized: only checks first few rows since keys are typically consistent.
 */
function collectNonAggregateKeys(rows: DataRow[]): Set<string> {
  const keys = new Set<string>()
  // Only check first 10 rows + a few random ones - keys are usually consistent
  const samplesToCheck = Math.min(rows.length, 20)
  const step = Math.max(1, Math.floor(rows.length / samplesToCheck))

  for (let i = 0; i < samplesToCheck && i < rows.length; i++) {
    const index = i * step
    const r = rows[index]
    if (!r) continue
    for (const k of Object.keys(r)) {
      if (k === "timestamp" || k === "value") continue
      keys.add(k)
    }
  }
  return keys
}

/**
 * Aggregates rows beyond the top-N selectors (from sorted_data_ids) into "other",
 * summing per timestamp (and per metric_id unless aggregateAcrossMetrics is true).
 */
export function aggregateBeyondLimitDynamic(
  payload: Payload,
  options: AggregateOptions
): Payload {
  const { limit, otherLabel = "other" } = options

  if (!payload) {
    return {
      data: [],
      sorted_data_ids: [],
    }
  }

  const sorted = payload.sorted_data_ids ?? []
  const data = payload.data ?? []

  // Pre-compute key mappings once
  const keyMappings = precomputeKeyMappings(sorted)

  // Top matcher with pre-computed mappings
  const isTop = buildTopMatcher(sorted, limit, keyMappings)

  // Partition
  const keptRows: DataRow[] = []
  const overflowRows: DataRow[] = []

  for (const row of data) {
    if (isTop(row)) {
      keptRows.push(row)
    } else {
      overflowRows.push(row)
    }
  }

  // Fast path: no overflow => return unchanged but still trim sorted list to top N
  if (overflowRows.length === 0) {
    return {
      data: keptRows.slice(),
      sorted_data_ids: sorted.slice(0, Math.max(0, limit)),
    }
  }

  // Aggregate overflow
  // Group key: timestamp + (metric_id?) depending on option
  type GroupKey = string
  const groups = new Map<
    GroupKey,
    { timestamp: string; metric_id?: string; value: number }
  >()

  for (const r of overflowRows) {
    // Use timestamp directly as key (no string interpolation needed)
    const group = groups.get(r.timestamp)
    const value = Number(r.value || 0)
    if (group) {
      group.value += value
    } else {
      groups.set(r.timestamp, {
        timestamp: r.timestamp,
        metric_id: r.metric_id ?? undefined,
        value,
      })
    }
  }

  // Determine which keys exist in data to stamp "other" on
  const allNonAggKeys = collectNonAggregateKeys(data)

  // Build "other" rows
  const otherRows: DataRow[] = []
  for (const group of groups.values()) {
    const out: DataRow = {
      timestamp: group.timestamp,
      value: group.value,
    }

    // Preserve metric_id only if not aggregating across metrics and it exists
    if (group.metric_id !== undefined) {
      out.metric_id = group.metric_id
    }

    // For every other key present in input rows (excluding timestamp, value, and possibly metric_id),
    // set to "other"
    for (const k of allNonAggKeys) {
      if (k === "metric_id") continue // already set or preserved
      out[k] = otherLabel
    }

    otherRows.push(out)
  }

  // Final data
  const outData = keptRows.concat(otherRows)

  // Sort all data by timestamp asc (optimized: timestamps are ISO strings, lexicographically sortable)
  outData.sort((a, b) => {
    const tsA = a.timestamp
    const tsB = b.timestamp
    return tsA < tsB ? -1 : tsA > tsB ? 1 : 0
  })

  // Build new sorted_data_ids:
  // - Take the first N entries as-is
  // - Append a synthetic "other" entry that covers ALL keys present in sorted entries,
  //   setting each to "other". This ensures downstream matchers can treat "other" as a group.
  const topSorted = sorted.slice(0, Math.max(0, limit))
  const allSortedKeys = new Set<string>()
  for (const sel of sorted) {
    for (const k of Object.keys(sel)) allSortedKeys.add(k)
  }
  const otherSelector: SortedEntry = {}
  for (const k of allSortedKeys)
    otherSelector[k as keyof SortedEntry] = otherLabel

  const outSorted =
    topSorted.length > 0 ? [...topSorted, otherSelector] : [otherSelector]

  return {
    data: outData,
    sorted_data_ids: outSorted,
  }
}
